import companyApi from '@src/@data/api/company';
import {
  AccessConfigResponse,
  FeatureFlagProductState,
  FeatureFlags,
} from '@src/@data/types/company';
import { UserRoles, UserRoleScopes } from '@src/@data/types/userRoles';
import { logOnError } from '@src/@data/utility/errors';
import { useCompany } from '@src/@data/store/CompanyProvider';
import { useAuth } from '@src/@data/store/AuthProvider';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

interface FeatureFlagsContextType {
  featureFlags?: FeatureFlags;
  userRole?: UserRoles;
  userScopes?: UserRoleScopes[];
  hasCovidVerifyAvailable: boolean;
  productsEnabledState: FeatureFlagProductState | undefined;
  areProductsAllowed: boolean;
  areSMSCampaignsEnabled: boolean;
  areFinancesAllowed: boolean;
  areStatisticsAllowed: boolean;
  areIntegrationsAllowed: boolean;
  isAccessManagementAllowed: boolean;
  isCompanyEditingAllowed: boolean;
  isReady: boolean;
  areActivityLogsAllowed: boolean;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType>({
  areProductsAllowed: false,
  areSMSCampaignsEnabled: false,
  areFinancesAllowed: false,
  areStatisticsAllowed: false,
  areIntegrationsAllowed: false,
  hasCovidVerifyAvailable: false,
  isAccessManagementAllowed: false,
  isCompanyEditingAllowed: false,
  productsEnabledState: undefined,
  isReady: false,
  areActivityLogsAllowed: false,
});

export const areReservationsNotificationsEnabled = (): boolean => {
  return (
    !!process.env.REACT_APP_FIREBASE_API_KEY &&
    !!process.env.REACT_APP_FIREBASE_VAPID_KEY &&
    !!process.env.REACT_APP_FIREBASE_PROJECT_ID &&
    !!process.env.REACT_APP_FIREBASE_SENDER_ID &&
    !!process.env.REACT_APP_FIREBASE_APP_ID &&
    !!process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
  );
};

export const isRecurringSubscriptionsEnabled = () =>
  process.env.REACT_APP_FEATURE_ENABLED_RECURRING_SUBSCRIPTIONS === 'true';

export const useFeatureFlags = () => ({
  ...useContext(FeatureFlagsContext),
  areReservationsNotificationsEnabled: areReservationsNotificationsEnabled(),
  isRecurringSubscriptionsEnabled: isRecurringSubscriptionsEnabled(),
});

export const FeatureFlagsProvider: React.FC = ({ children }) => {
  const { selectedCompanyId } = useCompany();
  const { isAdminBooklaUser } = useAuth();
  const [isReady, setIsReady] = useState(false);
  const [accessConfig, setAccessConfig] = useState<AccessConfigResponse>();

  const hasCovidVerifyAvailable = useMemo(
    () => !!accessConfig?.features.covidVerifyAvailable,
    [accessConfig?.features.covidVerifyAvailable]
  );
  const doesUserHaveFullAccess = useMemo(
    () => !!accessConfig && accessConfig.role !== UserRoles.worker,
    [accessConfig]
  );
  const getUserHasScope = useCallback(
    (scope: UserRoleScopes) => !!accessConfig?.scopes?.includes(scope),
    [accessConfig?.scopes]
  );
  const areProductsAllowed = useMemo(
    () =>
      isReady &&
      (doesUserHaveFullAccess || getUserHasScope(UserRoleScopes.products)),
    [isReady, doesUserHaveFullAccess, getUserHasScope]
  );
  const areFinancesAllowed = useMemo(
    () =>
      isReady &&
      (doesUserHaveFullAccess || getUserHasScope(UserRoleScopes.finances)),
    [isReady, doesUserHaveFullAccess, getUserHasScope]
  );
  const areStatisticsAllowed = useMemo(
    () =>
      isReady &&
      (doesUserHaveFullAccess || getUserHasScope(UserRoleScopes.stats)),
    [isReady, doesUserHaveFullAccess, getUserHasScope]
  );
  const areIntegrationsAllowed = useMemo(
    () =>
      isReady &&
      (doesUserHaveFullAccess || getUserHasScope(UserRoleScopes.integrations)),
    [isReady, doesUserHaveFullAccess, getUserHasScope]
  );
  const isAccessManagementAllowed = useMemo(
    () =>
      isReady &&
      (doesUserHaveFullAccess || getUserHasScope(UserRoleScopes.access)),
    [isReady, doesUserHaveFullAccess, getUserHasScope]
  );
  const isCompanyEditingAllowed = useMemo(
    () =>
      isReady &&
      (doesUserHaveFullAccess || getUserHasScope(UserRoleScopes.companyEdit)),
    [isReady, doesUserHaveFullAccess, getUserHasScope]
  );
  const productsEnabledState = useMemo(
    () => accessConfig?.features.products,
    [accessConfig?.features.products]
  );
  const areSMSCampaignsEnabled = useMemo(
    () => !!accessConfig?.features.sms_campaigns,
    [accessConfig?.features.sms_campaigns]
  );
  const areActivityLogsAllowed = useMemo(
    () =>
      (!!accessConfig?.role &&
        [UserRoles.owner, UserRoles.admin].includes(accessConfig.role)) ||
      isAdminBooklaUser,
    [accessConfig?.role, isAdminBooklaUser]
  );

  useEffect(() => {
    if (!selectedCompanyId) {
      return;
    }

    companyApi
      .getAccessConfig(selectedCompanyId)
      .then(setAccessConfig)
      .catch(logOnError)
      .finally(() => {
        setIsReady(true);
      });
  }, [selectedCompanyId]);

  return (
    <FeatureFlagsContext.Provider
      value={{
        featureFlags: accessConfig?.features,
        userRole: accessConfig?.role,
        userScopes: accessConfig?.scopes,
        hasCovidVerifyAvailable,
        productsEnabledState,
        areProductsAllowed,
        areSMSCampaignsEnabled,
        areFinancesAllowed,
        areStatisticsAllowed,
        areIntegrationsAllowed,
        isAccessManagementAllowed,
        isCompanyEditingAllowed,
        isReady,
        areActivityLogsAllowed,
      }}
    >
      {children}
    </FeatureFlagsContext.Provider>
  );
};
